import { useEffect, useRef } from "react";

/**
 * Parses the current chart route into immediately usable route state so
 * consumers do not need to wait for a syncing effect before rendering.
 *
 * @param {string} pathname - React Router pathname.
 * @param {string[]} ticketPageModes - Route segments treated as create-ticket modes.
 * @returns {{
 *   patientId: number|null,
 *   pageMode: string|null,
 *   orderId: number|null,
 *   parentOrderId: number|null
 * }}
 */
export function getChartRouteState(pathname, ticketPageModes = []) {
  const pathParts = pathname.split("/");
  const patientIdFromPath = pathParts[2] || null;
  const thirdSegment = pathParts[3] || null;
  const fourthSegment = pathParts[4] || null;
  const fifthSegment = pathParts[5] || null;
  const parsedPatientId =
    patientIdFromPath && !Number.isNaN(Number(patientIdFromPath))
      ? Number(patientIdFromPath)
      : null;
  const parsedOrderId =
    fourthSegment && !Number.isNaN(Number(fourthSegment))
      ? Number(fourthSegment)
      : null;

  if (thirdSegment && thirdSegment !== "order" && !fourthSegment) {
    return {
      patientId: parsedPatientId,
      pageMode: thirdSegment,
      orderId: null,
      parentOrderId: null,
    };
  }

  if (thirdSegment === "order" && parsedOrderId !== null && !fifthSegment) {
    return {
      patientId: parsedPatientId,
      pageMode: null,
      orderId: parsedOrderId,
      parentOrderId: null,
    };
  }

  if (thirdSegment === "order" && parsedOrderId !== null && fifthSegment) {
    return {
      patientId: parsedPatientId,
      pageMode: fifthSegment,
      orderId: ticketPageModes.includes(fifthSegment) ? null : parsedOrderId,
      parentOrderId: parsedOrderId,
    };
  }

  return {
    patientId: parsedPatientId,
    pageMode: null,
    orderId: null,
    parentOrderId: null,
  };
}

/**
 * Synchronizes chart routing state from the current URL and React Router history.
 * Parses pathname segments, updates page mode and order selection, applies loader
 * chart state from location state, and restores chart state when the user navigates
 * back with history.
 *
 * @param {Object} parameters
 * @param {import("history").History} parameters.history - React Router history.
 * @param {import("react-router").Location} parameters.location - React Router location.
 * @param {*} parameters.originalChart - Baseline chart used when restoring after a history pop.
 * @param {function(): void} parameters.onHistoryPopRestore - Resets chart and related UI state when the user pops history with a loaded chart.
 * @param {function(Object): void} parameters.applyLoaderChartFromLocation - Applies chart from navigation loader result on the location object.
 * @param {function(*): void} parameters.setUploadedFile - Clears uploaded file state on navigation.
 * @returns {void}
 */
export function useChartLocationAndPageMode({
  history,
  location,
  originalChart,
  onHistoryPopRestore,
  applyLoaderChartFromLocation,
  setUploadedFile,
}) {
  /**
   * The last navigation action reference.
   * @type {React.MutableRefObject<string>}
   */
  const lastNavigationActionRef = useRef(history.action);

  /**
   * Handles the history pop restore.
   * @returns {void}
   */
  useEffect(() => {
    const unlisten = history.listen((_, action) => {
      lastNavigationActionRef.current = action;

      if (action === "POP" && originalChart) {
        onHistoryPopRestore();
      }
    });

    return unlisten;
  }, [history, onHistoryPopRestore, originalChart]);

  /**
   * Handles the location and page mode.
   * @returns {void}
   */
  useEffect(() => {
    const isHistoryPopWithLoadedChart =
      lastNavigationActionRef.current === "POP" && !!originalChart;

    if (!isHistoryPopWithLoadedChart) {
      applyLoaderChartFromLocation(location);
    }
    setUploadedFile(null);
    lastNavigationActionRef.current = history.action;
    // Match prior behavior: only re-sync from the URL when history action or location changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional; see comment above
  }, [history.action, location]);
}
